import { createServerFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import { categories, categoriesKeys } from '@worldcoo/monolith/core';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';
import { Searcher } from '@worldcoo/shared/searcher';
import { getLegalContentHtml } from '@/content/legal-content';
import { cache, refetch } from './server-cache.server';
import {
	portalAuthorCacheOptions,
	portalMetadataCacheOptions,
	portalCampaignsCacheOptions,
	portalCampaignCacheOptions,
	portalCampaignMetadataCacheOptions,
	portalCampaignsAggregatesCacheOptions,
	worldcooGlobalStatsCacheOptions,
	recentDonationsCacheOptions,
} from './server-cache-options.server';
import cityToCountryJson from './city-iata-to-country.json';
import * as z from 'zod';
import { parseRequest, parseIp, requestContextSchema } from './server-functions-shared.server';

const cityToCountry: Record<string, string> = cityToCountryJson;

export const getRequestContext = createServerFn()
	.inputValidator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

export const getPortalAuthorFn = createServerFn()
	.inputValidator(requestContextSchema)
	.handler(({ data: { portalId } }) => {
		return cache(portalAuthorCacheOptions(portalId));
	});

export const getPortalMetadataFn = createServerFn()
	.inputValidator(requestContextSchema)
	.handler(({ data: { portalId, locale } }) => {
		return cache(portalMetadataCacheOptions(portalId, locale));
	});

export const getPortalCampaignMetadataFn = createServerFn()
	.inputValidator(z.object({ ...requestContextSchema.shape, campaignId: z.string() }))
	.handler(({ data: { portalId, locale, campaignId } }) => {
		return cache(portalCampaignMetadataCacheOptions(portalId, locale, campaignId));
	});

/**
 * Get aggregates for all campaigns in a portal with timeout handling
 * Returns undefined if the request times out (with console error)
 * This allows the UI to fallback gracefully when aggregates can't be loaded
 */
export const getPortalCampaignsAggregatesFn = createServerFn()
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(({ data: { portalId } }) => {
		return cache(portalCampaignsAggregatesCacheOptions(portalId));
	});

export const getPortalCampaignsFn = createServerFn()
	.inputValidator(
		z.object({
			...requestContextSchema.shape,
			query: z.string().optional(),
			year: z.number().optional(),
			state: z.literal('finished').optional(),
			causes: z.array(z.enum(categoriesKeys)).optional(),
			city: z.string().optional(),
		}),
	)
	.handler(async ({ data: { portalId, locale, query, year, state, causes, city } }) => {
		const result = await cache(portalCampaignsCacheOptions(portalId, locale));
		if ('notFound' in result) return result;

		const countryCode = city ? cityToCountry[city] : undefined;
		const normalizedQuery = query ? Searcher.normalizeString(query) : undefined;

		// Single pass through campaigns to collect all needed data
		const yearSet = new Set<number>();
		let activeCampaignsCount = 0;
		const filteredCampaigns: Array<{ campaign: (typeof result.campaigns)[0]; matchesCountry: boolean }> = [];

		for (const campaign of result.campaigns) {
			// Count active campaigns
			if (!campaign.finished) {
				activeCampaignsCount++;
			}

			// Collect years
			if (campaign.startDate) {
				const campaignYear = new Date(campaign.startDate).getFullYear();
				yearSet.add(campaignYear);
			}

			// Apply filters
			// Year filter
			if (year && campaign.startDate && new Date(campaign.startDate).getFullYear() !== year) {
				continue;
			}

			// Causes filter
			if (causes && causes.length > 0) {
				const hasCause = causes.some((cause) => campaign.categories.includes(categories[cause]));
				if (!hasCause) continue;
			}

			// Query filter
			if (normalizedQuery) {
				const normalizedValue = Searcher.normalizeString(
					`${campaign.id} ${campaign.name} ${campaign.ngos.map((ngo) => ngo.name).join(' ')}`,
				);
				if (!normalizedValue.includes(normalizedQuery)) {
					continue;
				}
			}

			// Track whether campaign matches country filter
			const matchesCountry = !countryCode || campaign.countryCode === countryCode;
			filteredCampaigns.push({ campaign, matchesCountry });
		}

		// Determine final campaigns based on country filter logic
		const hasCountryMatches = filteredCampaigns.some((item) => item.matchesCountry && !item.campaign.finished);
		const finalCampaigns: typeof result.campaigns = [];
		let filteredActiveCampaignsCount = 0;

		for (const { campaign, matchesCountry } of filteredCampaigns) {
			// If we have country code and no active matches, show all filtered campaigns
			// Otherwise, only show campaigns that match the country filter
			if (countryCode && !hasCountryMatches) {
				// Show all filtered campaigns (ignore country filter)
				finalCampaigns.push(campaign);
				if (!campaign.finished) filteredActiveCampaignsCount++;
			} else if (matchesCountry) {
				// Only show campaigns matching country
				finalCampaigns.push(campaign);
				if (!campaign.finished) filteredActiveCampaignsCount++;
			}
		}

		// Apply state filter to final campaigns
		const campaigns: typeof result.campaigns = [];
		for (const campaign of finalCampaigns) {
			// Show finished campaigns if there are no active campaigns
			if (activeCampaignsCount === 0) {
				campaigns.push(campaign);
				continue;
			}

			if (!state && campaign.finished) continue;
			if (state === 'finished' && !campaign.finished) continue;

			campaigns.push(campaign);
		}

		// There is not a wide support for toSorted() in the browser
		// oxlint-disable-next-line unicorn/no-array-sort
		const yearOptions = Array.from(yearSet).sort((a, b) => b - a);

		return {
			campaignsCount: result.campaigns.length,
			activeCampaignsCount,
			filteredCampaignsCount: finalCampaigns.length,
			filteredActiveCampaignsCount,
			campaigns,
			yearOptions,
		};
	});

export const getPortalCampaignFn = createServerFn()
	.inputValidator(z.object({ ...requestContextSchema.shape, campaignId: z.string() }))
	.handler(({ data: { portalId, locale, campaignId } }) => {
		return cache(portalCampaignCacheOptions(portalId, locale, campaignId));
	});

/**
 * www
 */
export const getWorldcooGlobalStatsFn = createServerFn().handler(() => {
	return cache(worldcooGlobalStatsCacheOptions());
});

export const getRecentDonationsFn = createServerFn()
	.inputValidator(
		z.object({
			windowKey: z.string(),
			mode: z.enum(['day', 'realtime']).default('day'),
		}),
	)
	.handler(({ data }) => {
		return cache(recentDonationsCacheOptions(data.windowKey, data.mode));
	});

/**
 * shared
 */
export const getLegalContentFn = createServerFn()
	.inputValidator(
		z.object({
			type: z.enum(['legal-disclaimer', 'privacy-policy', 'cookies', 'selection-criteria']),
			locale: z.string(),
		}),
	)
	.handler(({ data: { type, locale } }) => {
		return getLegalContentHtml(type, locale);
	});

export const logErrorFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			route: z.string().catch('unknown'),
			name: z.string().catch('UnknownError'),
			message: z.string().catch('(no message)'),
			stack: z.string().optional(),
		}),
	)
	.handler(({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();

		const logPayload = {
			type: 'PORTAL_FRONTEND_ERROR',
			timestamp: new Date().toISOString(),
			portalId: request.portalId,
			locale: request.locale,
			baseUrl: request.baseUrl,
			ip: requestIp,
			userAgent: getRequestHeader('User-Agent') ?? undefined,
			route: data.route,
			name: data.name,
			message: data.message,
			stack: data.stack,
		};

		console.error(`PORTAL_FRONTEND_ERROR: ${data.name} — ${data.message}`);
		console.info(logPayload);

		return { success: true, logged: true };
	});

export const refreshPortalAggregatesCacheFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(async ({ data: { portalId } }) => {
		// Only refresh aggregates - campaigns and campaign data no longer contain aggregates
		await refetch(portalCampaignsAggregatesCacheOptions(portalId));
	});
